// Ein Foto am Gerät aufbereiten (PLAN.md, Abschnitt 3, „Fotos und
// Dateiablage“; seit E2, Schritt 2 für die echte Besichtigung – in E1
// benutzten es die Technik-Proben, die es seit Schritt 6 nicht mehr gibt):
//
//  1. Die Aufnahmezeit aus dem Rohfoto lesen – VOR dem Verkleinern, danach
//     hat das Foto keine Metadaten mehr. Trägt es keine, gilt der Zeitpunkt
//     der Datei (Plan E2: „Rückfall lastModified“).
//  2. Verkleinern: lange Kante 2048 Pixel, JPEG. Der Browser zeichnet das
//     Bild dabei neu – Standort, Kamera und alle anderen Metadaten bleiben im
//     Rohfoto, das das Handy nie verlässt.
//  3. Was der Browser beim Neu-Kodieren selbst hineinschreibt – am iPhone
//     sind das Exif- und IPTC-Blöcke –, entfernt `jpegMetadatenEntfernen`
//     danach Byte für Byte, unabhängig vom Browser (seit O39, 19.10.2026;
//     DECISIONS.md, 18.10., Punkt 7).
//  4. Die Prüfsumme (SHA-256) des fertigen Fotos – sie ist sein Name am
//     Server.

import { jpegAufnahmezeit, jpegMetadatenEntfernen, type Standort } from "@happy-energy/kern";

/** Alle Fotos 2048 Pixel an der langen Kante – auch das Typenschild ist darin lesbar (DECISIONS.md, 18.10., Punkt 6). */
export const LANGE_KANTE = 2048;

export interface Verkleinert {
  datei: Blob;
  breite: number;
  hoehe: number;
  /** Welche Info-Blöcke der Browser hineingeschrieben hatte – jetzt entfernt. */
  entfernt: string[];
  /** Ob darin ein Standort stand. */
  standort: Standort;
}

export async function verkleinern(roh: Blob, langeKante = LANGE_KANTE, qualitaet = 0.85): Promise<Verkleinert> {
  // „from-image“: das Foto so drehen, wie das Handy es hielt.
  const bild = await createImageBitmap(roh, { imageOrientation: "from-image" });
  const faktor = Math.min(1, langeKante / Math.max(bild.width, bild.height));
  const breite = Math.round(bild.width * faktor);
  const hoehe = Math.round(bild.height * faktor);
  const leinwand = document.createElement("canvas");
  leinwand.width = breite;
  leinwand.height = hoehe;
  const stift = leinwand.getContext("2d");
  if (!stift) throw new Error("Zeichenfläche nicht verfügbar.");
  stift.drawImage(bild, 0, 0, breite, hoehe);
  bild.close();
  const kodiert = await new Promise<Blob>((fertig, fehler) =>
    leinwand.toBlob((b) => (b ? fertig(b) : fehler(new Error("JPEG konnte nicht erzeugt werden."))), "image/jpeg", qualitaet),
  );
  // Speicher der Zeichenfläche gleich freigeben – am iPhone ist er knapp.
  leinwand.width = 0;
  leinwand.height = 0;
  const bereinigt = jpegMetadatenEntfernen(new Uint8Array(await kodiert.arrayBuffer()));
  if (!bereinigt.ok) throw new Error(`Der Browser hat kein brauchbares JPEG erzeugt (${bereinigt.grund}).`);
  const datei = bereinigt.entfernt.length === 0 ? kodiert : new Blob([bereinigt.daten as BlobPart], { type: "image/jpeg" });
  return { datei, breite, hoehe, entfernt: bereinigt.entfernt, standort: bereinigt.standort };
}

/** Die Prüfsumme einer Datei: SHA-256 als 64 Zeichen 0–9, a–f – wie der Server sie nachrechnet. */
export async function pruefsummeBerechnen(datei: Blob | ArrayBuffer): Promise<string> {
  const summe = await crypto.subtle.digest("SHA-256", datei instanceof ArrayBuffer ? datei : await datei.arrayBuffer());
  return Array.from(new Uint8Array(summe), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Der Exif-Block steht bei jeder Kamera vorne – so viel vom Rohfoto genügt, um die Zeit zu lesen. */
const ANFANG_FUER_EXIF = 512 * 1024;

/**
 * Wann das Foto aufgenommen wurde – als Zeitpunkt (ISO, UTC). Aus dem
 * Exif-Block des Rohfotos; fehlt er, der Zeitpunkt der Datei; null, wenn
 * es auch den nicht gibt.
 */
export async function aufnahmezeitLesen(roh: Blob & { lastModified?: number }): Promise<string | null> {
  const exif = jpegAufnahmezeit(new Uint8Array(await roh.slice(0, ANFANG_FUER_EXIF).arrayBuffer()));
  // Ohne Zeitzone im Foto liest der Browser die Zeit als Ortszeit des Geräts – dort wurde fotografiert.
  const zeit = exif !== null ? new Date(exif) : roh.lastModified ? new Date(roh.lastModified) : null;
  return zeit && !Number.isNaN(zeit.getTime()) ? zeit.toISOString() : null;
}

/** Ein Foto, fertig zum Ablegen am Gerät und zum Senden. */
export interface FotoBereit {
  /** Das verkleinerte JPEG ohne Metadaten. */
  datei: Blob;
  pruefsumme: string;
  breite: number;
  hoehe: number;
  groesse: number;
  aufnahmezeit: string | null;
}

/** Vom Rohfoto (Kamera oder Fotos-App) zum fertigen Foto: Zeit lesen, verkleinern, Prüfsumme. */
export async function fotoVorbereiten(roh: File): Promise<FotoBereit> {
  const aufnahmezeit = await aufnahmezeitLesen(roh);
  const klein = await verkleinern(roh);
  return {
    datei: klein.datei,
    pruefsumme: await pruefsummeBerechnen(klein.datei),
    breite: klein.breite,
    hoehe: klein.hoehe,
    groesse: klein.datei.size,
    aufnahmezeit,
  };
}
